import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAttachment,
  useThreadRuntime,
} from "@assistant-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { AttachmentInfo, WidgetItem } from "./useSuperdocRuntime";
import FollowUpQuestions from "./widgets/FollowUpQuestions";
import SourcesCitation from "./widgets/SourcesCitation";

// ── Types ────────────────────────────────────────────────────────────────

interface InternalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  widgets?: WidgetItem[];
  attachments?: AttachmentInfo[];
}

interface SuperdocThreadProps {
  runtime: ReturnType<typeof import("@assistant-ui/react").useExternalStoreRuntime>;
  messages: InternalMessage[];
  isRunning: boolean;
  onSendFollowup: (question: string) => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
  threadList?: Array<{ id: string; title: string }>;
  onSwitchThread?: (threadId: string) => void;
  onNewThread?: () => void;
  baseUrl?: string;
  getHeaders?: () => Record<string, string>;
}

// ── Start Screen ─────────────────────────────────────────────────────────

const startPrompts = [
  {
    label: "Summarize today's notes",
    prompt: "Can you summarize the notes I created today?",
    icon: "document",
  },
  {
    label: "Help with diagnosis",
    prompt: "Help me think through a differential diagnosis for my current patient.",
    icon: "lightbulb",
  },
  {
    label: "Treatment suggestions",
    prompt: "What treatment options should I consider for my patient?",
    icon: "sparkle",
  },
];

const PromptIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === "document") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9.333 1.333H4A1.333 1.333 0 002.667 2.667v10.666A1.333 1.333 0 004 14.667h8A1.333 1.333 0 0013.333 13.333V5.333L9.333 1.333z"
          stroke="#6366F1"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M9.333 1.333v4h4" stroke="#6366F1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "lightbulb") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 14h4M8 1.333A4.667 4.667 0 005.333 9.667V10a.667.667 0 00.667.667h4a.667.667 0 00.667-.667v-.333A4.667 4.667 0 008 1.333z"
          stroke="#6366F1"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // sparkle
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0L9.6 5.6L15 7L9.6 8.4L8 14L6.4 8.4L1 7L6.4 5.6L8 0Z" fill="#6366F1" />
    </svg>
  );
};

const StartScreen: React.FC = () => {
  const threadRuntime = useThreadRuntime();

  const handlePromptClick = useCallback(
    (prompt: string) => {
      threadRuntime.append({
        role: "user",
        content: [{ type: "text", text: prompt }],
      });
    },
    [threadRuntime],
  );

  return (
    <div style={startScreenStyles.container}>
      <div style={startScreenStyles.greeting}>Hi, I'm Superdoc! How can I help you today?</div>
      <div style={startScreenStyles.prompts}>
        {startPrompts.map((p) => (
          <button
            key={p.label}
            type="button"
            style={startScreenStyles.promptButton}
            onClick={() => handlePromptClick(p.prompt)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F0EEFF";
              e.currentTarget.style.borderColor = "#6366F1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#FFFFFF";
              e.currentTarget.style.borderColor = "#E5E7EB";
            }}
          >
            <PromptIcon type={p.icon} />
            <span style={startScreenStyles.promptLabel}>{p.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const startScreenStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "24px",
    gap: "24px",
  },
  greeting: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#111827",
    textAlign: "center",
  },
  prompts: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    maxWidth: "320px",
  },
  promptButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s",
    fontFamily: "inherit",
  },
  promptLabel: {
    fontSize: "14px",
    color: "#374151",
  },
};

// ── User Message ─────────────────────────────────────────────────────────

const UserMessage: React.FC = () => (
  <div style={msgStyles.userContainer}>
    <div style={msgStyles.userBubble}>
      <MessagePrimitive.Content components={{ Text: ({ text }: { text: string }) => <span>{text}</span> }} />
    </div>
  </div>
);

// ── Assistant Message ────────────────────────────────────────────────────

const AssistantMessageContent: React.FC<{
  messages: InternalMessage[];
  onSendFollowup: (q: string) => void;
}> = ({ messages, onSendFollowup }) => {
  // We need access to the current message index to find widgets.
  // assistant-ui gives us the message via context.
  // Since we can't directly access the internal message with widgets from
  // the primitive, we'll render widgets after the content using a custom wrapper.
  return (
    <MessagePrimitive.Content
      components={{
        Text: ({ text }: { text: string }) => <AssistantText text={text} />,
      }}
    />
  );
};

const AssistantText: React.FC<{ text: string }> = ({ text }: { text: string }) => {
  // Render markdown-like content (basic formatting)
  // The backend sends plain text + markdown. We'll render it with basic support.
  return <div style={msgStyles.assistantText} dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }} />;
};

function formatMarkdown(text: string): string {
  if (!text) return "";

  const lines = text.split("\n");
  const output: string[] = [];
  let inUl = false;
  let inOl = false;

  for (const rawLine of lines) {
    // Escape HTML
    const line = rawLine.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const ulMatch = line.match(/^- (.+)$/);
    const olMatch = line.match(/^(\d+)\. (.+)$/);

    if (ulMatch) {
      if (inOl) {
        output.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        output.push('<ul style="margin:4px 0;padding-left:20px">');
        inUl = true;
      }
      output.push(`<li>${applyInline(ulMatch[1])}</li>`);
      continue;
    }

    if (olMatch) {
      if (inUl) {
        output.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        output.push('<ol style="margin:4px 0;padding-left:20px">');
        inOl = true;
      }
      output.push(`<li>${applyInline(olMatch[2])}</li>`);
      continue;
    }

    // Close any open list
    if (inUl) {
      output.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      output.push("</ol>");
      inOl = false;
    }

    // Empty line = paragraph break
    if (line.trim() === "") {
      output.push("<br/>");
      continue;
    }

    // Headers
    const h3Match = line.match(/^### (.+)$/);
    if (h3Match) {
      output.push(`<h4 style="font-size:14px;font-weight:600;margin:12px 0 4px">${applyInline(h3Match[1])}</h4>`);
      continue;
    }
    const h2Match = line.match(/^## (.+)$/);
    if (h2Match) {
      output.push(`<h3 style="font-size:15px;font-weight:600;margin:12px 0 4px">${applyInline(h2Match[1])}</h3>`);
      continue;
    }
    const h1Match = line.match(/^# (.+)$/);
    if (h1Match) {
      output.push(`<h2 style="font-size:16px;font-weight:600;margin:12px 0 4px">${applyInline(h1Match[1])}</h2>`);
      continue;
    }

    // Horizontal rule
    if (line.trim() === "---") {
      output.push('<hr style="border:none;border-top:1px solid #E5E7EB;margin:12px 0"/>');
      continue;
    }

    // Normal line
    output.push(applyInline(line) + "<br/>");
  }

  if (inUl) output.push("</ul>");
  if (inOl) output.push("</ol>");

  // Remove trailing <br/>
  const result = output.join("\n");
  return result.replace(/(<br\/>)+$/, "");
}

function applyInline(text: string): string {
  let s = text;
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(
    /`(.+?)`/g,
    '<code style="background:#F3F4F6;padding:1px 4px;border-radius:4px;font-size:12px">$1</code>',
  );
  return s;
}

// ── Widgets Wrapper ──────────────────────────────────────────────────────

const MessageWidgets: React.FC<{
  messageId: string;
  messages: InternalMessage[];
  onSendFollowup: (q: string) => void;
}> = ({ messageId, messages, onSendFollowup }) => {
  const msg = messages.find((m) => m.id === messageId);
  if (!msg?.widgets?.length) return null;

  return (
    <>
      {msg.widgets.map((widget) => {
        if (widget.type === "sources") {
          return <SourcesCitation key={widget.id} sources={widget.data as Array<{ title: string; url?: string }>} />;
        }
        if (widget.type === "followup_questions") {
          return (
            <FollowUpQuestions key={widget.id} questions={widget.data as string[]} onSendFollowup={onSendFollowup} />
          );
        }
        return null;
      })}
    </>
  );
};

// ── User Attachment Chip (on sent messages) ──────────────────────────────

const UserAttachmentChip: React.FC<{ attachment: AttachmentInfo }> = ({ attachment }) => (
  <div style={msgStyles.attachmentChip}>
    <AttachmentIcon contentType={attachment.contentType} />
    <span style={msgStyles.attachmentChipName}>{attachment.filename}</span>
    {attachment.size != null && <span style={msgStyles.attachmentChipSize}>{formatFileSize(attachment.size)}</span>}
  </div>
);

// ── Attachment Preview ────────────────────────────────────────────────────

const AttachmentIcon: React.FC<{ contentType?: string }> = ({ contentType }) => {
  if (contentType?.startsWith("image/")) {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="14" height="14" rx="2" stroke="#6366F1" strokeWidth="1.2" />
        <circle cx="5.5" cy="5.5" r="1.5" fill="#6366F1" />
        <path
          d="M1 11l3.5-3.5L8 11l3-3 4 4"
          stroke="#6366F1"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.333 1.333H4A1.333 1.333 0 002.667 2.667v10.666A1.333 1.333 0 004 14.667h8A1.333 1.333 0 0013.333 13.333V5.333L9.333 1.333z"
        stroke="#6366F1"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.333 1.333v4h4" stroke="#6366F1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ComposerAttachmentPreview: React.FC = () => {
  const attachment = useAttachment();
  return (
    <div style={composerStyles.attachmentChip}>
      <AttachmentIcon contentType={attachment.contentType} />
      <span style={composerStyles.attachmentName}>{attachment.name}</span>
      {attachment.file && <span style={composerStyles.attachmentSize}>{formatFileSize(attachment.file.size)}</span>}
    </div>
  );
};

// ── Composer ──────────────────────────────────────────────────────────────

const SuperdocComposer: React.FC = () => (
  <div style={composerStyles.wrapper}>
    <ComposerPrimitive.Root style={composerStyles.root as React.CSSProperties}>
      {/* Pending attachments preview */}
      <ComposerPrimitive.Attachments
        components={{
          Attachment: ComposerAttachmentPreview,
        }}
      />
      <div style={composerStyles.inputRow}>
        <ComposerPrimitive.AddAttachment style={composerStyles.attachButton} title="Attach file">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14 8.267l-5.578 5.578a3.556 3.556 0 01-5.028-5.028l5.578-5.578a2.37 2.37 0 013.352 3.352L6.75 12.17a1.185 1.185 0 01-1.676-1.676l5.15-5.15"
              stroke="#6B7280"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ComposerPrimitive.AddAttachment>
        <ComposerPrimitive.Input
          placeholder="Ask Superdoc anything..."
          rows={1}
          maxRows={5}
          autoFocus={false}
          className="superdoc-composer-input"
        />
        <ComposerPrimitive.If dictation>
          <ComposerPrimitive.StopDictation style={composerStyles.micButtonActive} title="Stop dictation">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="10" height="10" rx="2" fill="white" />
            </svg>
          </ComposerPrimitive.StopDictation>
        </ComposerPrimitive.If>
        <ComposerPrimitive.If dictation={false}>
          <ComposerPrimitive.Dictate style={composerStyles.micButton} title="Voice input">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8 1a2.667 2.667 0 00-2.667 2.667v4a2.667 2.667 0 005.334 0v-4A2.667 2.667 0 008 1z"
                stroke="#6B7280"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.667 6.667v1a4.667 4.667 0 01-9.334 0v-1M8 12.333V15"
                stroke="#6B7280"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ComposerPrimitive.Dictate>
        </ComposerPrimitive.If>
        <ComposerPrimitive.Send style={composerStyles.sendButton}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.667 1.333L7.333 8.667M14.667 1.333l-4.334 13.334-2.666-6-6-2.667 13-4.667z"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
    <div style={composerStyles.disclaimer}>Superdoc is experimental. Verify everything.</div>
  </div>
);

const composerStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    padding: "8px 16px 6px",
    borderTop: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    borderRadius: "20px",
    border: "1px solid #D1D5DB",
    padding: "4px 4px 4px 16px",
    backgroundColor: "#FFFFFF",
    transition: "border-color 0.15s",
  },
  inputRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "4px",
    width: "100%",
  },
  attachButton: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color 0.15s",
  },
  micButton: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color 0.15s",
  },
  micButtonActive: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#EF4444",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    animation: "superdoc-pulse 1.5s infinite",
  },
  sendButton: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#6366F1",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color 0.15s",
  },
  attachmentChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "12px",
    backgroundColor: "#F3F4F6",
    border: "1px solid #E5E7EB",
    fontSize: "12px",
    margin: "2px 4px",
  },
  attachmentName: {
    color: "#374151",
    maxWidth: "120px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  attachmentSize: {
    color: "#9CA3AF",
    fontSize: "11px",
    flexShrink: 0,
  },
  disclaimer: {
    fontSize: "10px",
    color: "#C0C4CC",
    textAlign: "center" as const,
    marginTop: "2px",
    lineHeight: "1",
  },
};

// ── Typing Indicator ─────────────────────────────────────────────────────

const TypingIndicator: React.FC = () => (
  <div style={msgStyles.assistantContainer}>
    <div style={msgStyles.assistantBubble}>
      <div style={typingStyles.container}>
        <div style={{ ...typingStyles.dot, animationDelay: "0ms" }} />
        <div style={{ ...typingStyles.dot, animationDelay: "150ms" }} />
        <div style={{ ...typingStyles.dot, animationDelay: "300ms" }} />
      </div>
      <style>{`
        @keyframes superdoc-typing-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
        @keyframes superdoc-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  </div>
);

const typingStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    gap: "4px",
    padding: "4px 0",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#9CA3AF",
    animation: "superdoc-typing-bounce 1.2s infinite",
  },
};

// ── History Panel ────────────────────────────────────────────────────────

const HistoryPanel: React.FC<{
  threads: Array<{ id: string; title: string }>;
  currentThreadId: string | null;
  onSwitchThread: (id: string) => void;
  onNewThread: () => void;
  onClose: () => void;
}> = ({ threads, currentThreadId, onSwitchThread, onNewThread, onClose }) => (
  <div style={historyStyles.overlay} onClick={onClose}>
    <div style={historyStyles.panel} onClick={(e) => e.stopPropagation()}>
      <div style={historyStyles.header}>
        <span style={historyStyles.headerTitle}>History</span>
        <button type="button" style={historyStyles.newButton} onClick={onNewThread}>
          + New Chat
        </button>
      </div>
      <div style={historyStyles.list}>
        {threads.map((t) => (
          <button
            key={t.id}
            type="button"
            style={{
              ...historyStyles.threadItem,
              backgroundColor: t.id === currentThreadId ? "#EEF2FF" : "transparent",
            }}
            onClick={() => {
              onSwitchThread(t.id);
              onClose();
            }}
          >
            <span style={historyStyles.threadTitle}>{t.title || "Untitled conversation"}</span>
          </button>
        ))}
        {threads.length === 0 && <div style={historyStyles.empty}>No previous conversations</div>}
      </div>
    </div>
  </div>
);

const historyStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 10,
    display: "flex",
  },
  panel: {
    width: "280px",
    height: "100%",
    backgroundColor: "#FFFFFF",
    boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    borderBottom: "1px solid #E5E7EB",
  },
  headerTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#111827",
  },
  newButton: {
    fontSize: "13px",
    color: "#6366F1",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    fontFamily: "inherit",
  },
  list: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "8px",
  },
  threadItem: {
    display: "block",
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    textAlign: "left" as const,
    fontFamily: "inherit",
    transition: "background-color 0.15s",
  },
  threadTitle: {
    fontSize: "13px",
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    display: "block",
  },
  empty: {
    fontSize: "13px",
    color: "#9CA3AF",
    textAlign: "center" as const,
    padding: "24px",
  },
};

// ── Header ───────────────────────────────────────────────────────────────

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 1.333h4.667V6M6 14.667H1.333V10M14.667 1.333L9.333 6.667M1.333 14.667l5.334-5.334"
      stroke="#6B7280"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CollapseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.667 10H1.333v4.667M11.333 6h3.334V1.333M1.333 14.667l5.334-5.334M14.667 1.333l-5.334 5.334"
      stroke="#6B7280"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HistoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 5.333V8l2 1M14 8A6 6 0 112 8a6 6 0 0112 0z"
      stroke="#6B7280"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ThreadHeader: React.FC<{
  expanded?: boolean;
  onToggleExpand?: () => void;
  onToggleHistory: () => void;
}> = ({ expanded, onToggleExpand, onToggleHistory }) => (
  <div style={headerStyles.container}>
    <button type="button" style={headerStyles.iconButton} onClick={onToggleHistory} title="Chat history">
      <HistoryIcon />
    </button>
    <div style={{ flex: 1 }} />
    {onToggleExpand && (
      <button
        type="button"
        style={headerStyles.iconButton}
        onClick={onToggleExpand}
        title={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? <CollapseIcon /> : <ExpandIcon />}
      </button>
    )}
  </div>
);

const headerStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    minHeight: "40px",
  },
  iconButton: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.15s",
  },
};

// ── Message styles ───────────────────────────────────────────────────────

const msgStyles: Record<string, React.CSSProperties> = {
  userContainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "4px 16px",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  userBubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: "18px 18px 4px 18px",
    backgroundColor: "#6366F1",
    color: "#FFFFFF",
    fontSize: "14px",
    lineHeight: "1.5",
    wordBreak: "break-word" as const,
    textAlign: "left" as const,
  },
  assistantContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    padding: "4px 16px",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  assistantBubble: {
    maxWidth: "90%",
    padding: "10px 14px",
    borderRadius: "18px 18px 18px 4px",
    backgroundColor: "#FFFFFF",
    color: "#111827",
    fontSize: "14px",
    lineHeight: "1.5",
    wordBreak: "break-word" as const,
    border: "1px solid #E5E7EB",
    textAlign: "left" as const,
  },
  assistantText: {
    fontSize: "14px",
    lineHeight: "1.6",
    color: "#111827",
    textAlign: "left" as const,
  },
  widgetsContainer: {
    padding: "0 16px",
    width: "100%",
    boxSizing: "border-box" as const,
    maxWidth: "90%",
  },
  userAttachments: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "4px",
    marginTop: "6px",
  },
  attachmentChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px 8px",
    borderRadius: "8px",
    backgroundColor: "rgba(255,255,255,0.2)",
    fontSize: "12px",
    cursor: "default",
  },
  attachmentChipName: {
    maxWidth: "100px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    color: "inherit",
  },
  attachmentChipSize: {
    opacity: 0.7,
    fontSize: "11px",
    flexShrink: 0,
  },
};

// ── Main Thread Component ────────────────────────────────────────────────

const SuperdocThread: React.FC<SuperdocThreadProps> = ({
  runtime,
  messages,
  isRunning,
  onSendFollowup,
  expanded,
  onToggleExpand,
  threadList,
  onSwitchThread,
  onNewThread,
  baseUrl: _baseUrl,
  getHeaders: _getHeaders,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [threadListData, setThreadListData] = useState<Array<{ id: string; title: string }>>(threadList || []);
  const viewportRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  }, [messages, isRunning]);

  const handleToggleHistory = useCallback(async () => {
    setShowHistory((prev) => !prev);
  }, []);

  // Determine the current thread ID from the latest message
  const currentThreadId = messages.length > 0 ? null : null; // We don't track this here

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <div style={threadStyles.container}>
        <ThreadHeader expanded={expanded} onToggleExpand={onToggleExpand} onToggleHistory={handleToggleHistory} />

        <div style={threadStyles.viewport} ref={viewportRef}>
          {messages.length === 0 && !isRunning ? (
            <StartScreen />
          ) : (
            <div style={threadStyles.messageList}>
              {messages.map((msg) => (
                <React.Fragment key={msg.id}>
                  {msg.role === "user" ? (
                    <div style={msgStyles.userContainer}>
                      <div style={msgStyles.userBubble}>
                        {msg.content}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div style={msgStyles.userAttachments}>
                            {msg.attachments.map((att) => (
                              <UserAttachmentChip key={att.id} attachment={att} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={msgStyles.assistantContainer}>
                      <div style={msgStyles.assistantBubble}>
                        <AssistantText text={msg.content} />
                      </div>
                      {msg.widgets && msg.widgets.length > 0 && (
                        <MessageWidgets messageId={msg.id} messages={messages} onSendFollowup={onSendFollowup} />
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {isRunning && messages[messages.length - 1]?.role !== "assistant" && <TypingIndicator />}
              {isRunning &&
                messages[messages.length - 1]?.role === "assistant" &&
                messages[messages.length - 1]?.content === "" && <TypingIndicator />}
            </div>
          )}
        </div>

        <SuperdocComposer />

        {showHistory && onSwitchThread && onNewThread && (
          <HistoryPanel
            threads={threadListData}
            currentThreadId={null}
            onSwitchThread={onSwitchThread}
            onNewThread={() => {
              onNewThread();
              setShowHistory(false);
            }}
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>
    </AssistantRuntimeProvider>
  );
};

const threadStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  viewport: {
    flex: 1,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
  },
  messageList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "4px",
    padding: "12px 0",
    width: "100%",
  },
};

export default SuperdocThread;
